import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type Channel = "email" | "sms" | "whatsapp";

const SMS_PROVIDERS = ["Twilio", "MessageBird", "Vonage", "MSG91"] as const;

type SmsProvider = (typeof SMS_PROVIDERS)[number];

const channels: {
  id: Channel;
  title: string;
  description: string;
  icon: typeof Mail;
  iconClass: string;
  activeClass: string;
}[] = [
  {
    id: "email",
    title: "Email",
    description: "Connect with your SMTP host and credentials.",
    icon: Mail,
    iconClass: "bg-blue-100 text-blue-600",
    activeClass: "border-blue-500 bg-blue-50",
  },
  {
    id: "sms",
    title: "SMS",
    description: "Choose a provider, then add the API key.",
    icon: Phone,
    iconClass: "bg-indigo-100 text-indigo-600",
    activeClass: "border-indigo-500 bg-indigo-50",
  },
  {
    id: "whatsapp",
    title: "WhatsApp",
    description: "Connect a Twilio account.",
    icon: MessageCircle,
    iconClass: "bg-emerald-100 text-emerald-600",
    activeClass: "border-emerald-500 bg-emerald-50",
  },
];

export function CommunicationSetting() {
  const [activeChannel, setActiveChannel] = useState<Channel>("email");
  const [emailData, setEmailData] = useState({
    host: "",
    port: "",
    username: "",
    password: "",
    fromEmail: "",
  });
  const [smsProvider, setSmsProvider] = useState<SmsProvider | "">("");
  const [smsApiKey, setSmsApiKey] = useState("");
  const [whatsappData, setWhatsappData] = useState({
    accountId: "",
    privateKey: "",
  });

  const saveEmail = () => {
    if (!emailData.host.trim() || !emailData.port.trim() || !emailData.username.trim()) {
      toast.error("Enter the SMTP host, port, and username.");
      return;
    }
    toast.success("Email SMTP details saved.");
  };

  const saveSms = () => {
    if (!smsProvider) {
      toast.error("Choose an SMS provider.");
      return;
    }
    if (!smsApiKey.trim()) {
      toast.error("Enter the API key for the selected provider.");
      return;
    }
    toast.success(`${smsProvider} connected for SMS.`);
  };

  const saveWhatsapp = () => {
    if (!whatsappData.accountId.trim() || !whatsappData.privateKey.trim()) {
      toast.error("Enter the Twilio account ID and private key.");
      return;
    }
    toast.success("WhatsApp Twilio account saved.");
  };

  return (
    <Card className="border-slate-200 shadow-none">
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {channels.map((channel) => {
            const Icon = channel.icon;
            const selected = activeChannel === channel.id;
            return (
              <button
                key={channel.id}
                type="button"
                onClick={() => setActiveChannel(channel.id)}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-4 text-left transition-colors",
                  selected
                    ? channel.activeClass
                    : "border-slate-200 hover:border-slate-300",
                )}
              >
                <div
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-md",
                    channel.iconClass,
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <span className="block text-sm font-semibold">{channel.title}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {channel.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {activeChannel === "email" && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-800">SMTP details</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="smtp-host">SMTP host</Label>
                <Input
                  id="smtp-host"
                  value={emailData.host}
                  onChange={(event) =>
                    setEmailData({ ...emailData, host: event.target.value })
                  }
                  placeholder="smtp.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-port">Port</Label>
                <Input
                  id="smtp-port"
                  inputMode="numeric"
                  value={emailData.port}
                  onChange={(event) =>
                    setEmailData({ ...emailData, port: event.target.value })
                  }
                  placeholder="587"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-username">Username</Label>
                <Input
                  id="smtp-username"
                  value={emailData.username}
                  onChange={(event) =>
                    setEmailData({ ...emailData, username: event.target.value })
                  }
                  placeholder="SMTP username"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-password">Password</Label>
                <Input
                  id="smtp-password"
                  type="password"
                  value={emailData.password}
                  onChange={(event) =>
                    setEmailData({ ...emailData, password: event.target.value })
                  }
                  placeholder="SMTP password"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="smtp-from">From email</Label>
                <Input
                  id="smtp-from"
                  type="email"
                  value={emailData.fromEmail}
                  onChange={(event) =>
                    setEmailData({ ...emailData, fromEmail: event.target.value })
                  }
                  placeholder="rewards@example.com"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={saveEmail}
              className="bg-green-600 hover:bg-green-700"
            >
              Save SMTP details
            </Button>
          </div>
        )}

        {activeChannel === "sms" && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-800">SMS provider</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SMS_PROVIDERS.map((provider) => {
                const selected = smsProvider === provider;
                return (
                  <button
                    key={provider}
                    type="button"
                    onClick={() => setSmsProvider(provider)}
                    className={cn(
                      "rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors",
                      selected
                        ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                        : "border-slate-200 text-slate-800 hover:border-slate-300",
                    )}
                  >
                    {provider}
                  </button>
                );
              })}
            </div>
            {smsProvider && (
              <div className="space-y-2">
                <Label htmlFor="sms-api-key">{smsProvider} API key</Label>
                <Input
                  id="sms-api-key"
                  type="password"
                  value={smsApiKey}
                  onChange={(event) => setSmsApiKey(event.target.value)}
                  placeholder={`Enter the ${smsProvider} API key`}
                  autoComplete="off"
                />
              </div>
            )}
            <Button
              type="button"
              onClick={saveSms}
              className="bg-green-600 hover:bg-green-700"
            >
              Save SMS connection
            </Button>
          </div>
        )}

        {activeChannel === "whatsapp" && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-800">Twilio</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="twilio-account-id">Twilio account ID</Label>
                <Input
                  id="twilio-account-id"
                  value={whatsappData.accountId}
                  onChange={(event) =>
                    setWhatsappData({
                      ...whatsappData,
                      accountId: event.target.value,
                    })
                  }
                  placeholder="Account SID"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="twilio-private-key">Private key</Label>
                <Input
                  id="twilio-private-key"
                  type="password"
                  value={whatsappData.privateKey}
                  onChange={(event) =>
                    setWhatsappData({
                      ...whatsappData,
                      privateKey: event.target.value,
                    })
                  }
                  placeholder="Twilio private key"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={saveWhatsapp}
              className="bg-green-600 hover:bg-green-700"
            >
              Save WhatsApp connection
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
